import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { BRAND, STATS } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";

export const HeroSection = ({ onRegister, onSignin }) => {
  const { user } = useAuth();
  const [phone, setPhone] = useState("");

  const submit = (e) => {
    e.preventDefault();
    onRegister(phone);
  };

  return (
    <section id="home" data-testid="hero-section" className="relative min-h-[92vh] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img src={BRAND.heroImage} alt="Kenyan talent" className="h-full w-full object-cover object-center" />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(105deg, rgba(5,34,23,0.96) 0%, rgba(8,51,35,0.85) 45%, rgba(8,51,35,0.35) 100%)" }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16 w-full">
        <div className="max-w-2xl">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full bg-talanta-gold/15 border border-talanta-gold/30 px-4 py-1.5 text-talanta-gold text-xs font-semibold uppercase tracking-widest"
          >
            <Sparkles className="h-3.5 w-3.5" /> The national talent economy
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.08 }}
            data-testid="hero-title"
            className="mt-6 font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          >
            Monetizing Talent,
            <br />
            <span className="text-talanta-terracottaLight">Empowering</span>{" "}
            <span className="text-talanta-gold">Kenya</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.16 }}
            className="mt-5 text-lg text-white/80 max-w-xl"
          >
            One Talanta ID unlocks every pillar — Sport, Digital, Soko, Education, Gaming and Broadcast.
            One identity for the whole talent economy.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.24 }}
            className="mt-8"
          >
            {user ? (
              <a
                href="#pillars"
                data-testid="hero-cta-register"
                className="inline-flex items-center gap-2 rounded-full bg-talanta-red hover:bg-talanta-red/90 px-7 py-3.5 text-white font-semibold shadow-xl shadow-talanta-red/30 transition-all hover:scale-[1.03]"
              >
                Explore the pillars <ArrowRight className="h-5 w-5" />
              </a>
            ) : (
              <>
                <form
                  onSubmit={submit}
                  className="flex flex-col sm:flex-row gap-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 p-2 max-w-md"
                >
                  <input
                    data-testid="hero-phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    inputMode="tel"
                    placeholder="07XX XXX XXX"
                    className="flex-1 bg-transparent px-4 py-3 text-white placeholder-white/50 outline-none font-medium"
                  />
                  <button
                    type="submit"
                    data-testid="hero-cta-register"
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-talanta-red hover:bg-talanta-red/90 px-6 py-3 text-white font-semibold whitespace-nowrap transition-all hover:scale-[1.03]"
                  >
                    Get my ID <ArrowRight className="h-4 w-4" />
                  </button>
                </form>
                <p className="mt-3 text-sm text-white/60">
                  Already have a One Talanta ID?{" "}
                  <button type="button" data-testid="hero-signin-link" onClick={onSignin} className="font-semibold text-talanta-gold hover:underline underline-offset-4">
                    Sign in
                  </button>
                </p>
              </>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            data-testid="hero-stats-ticker"
            className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-x-10 gap-y-6 max-w-xl"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <div className="font-display font-extrabold text-2xl sm:text-3xl text-talanta-gold whitespace-nowrap">{s.value}</div>
                <div className="text-xs uppercase tracking-wider text-white/60 mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
